import getCommandBarButtons from './commandBar/getCommandBarButtons';
import SxSCustomView from './SxSCustomView';
import getCustomPropertiesForSigs from '../utils/getCustomPropertiesForSigs';
import { observer } from 'mobx-react-lite';
import { isFeatureEnabled } from 'owa-feature-flags';
import { composeStore } from 'owa-mail-compose-store';
import { FullCompose } from 'owa-mail-compose-view';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import popOutSxS from 'owa-mail-popout-sxs/lib/utils/popOutSxS';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import { ConversationReadingPane, ItemReadingPane } from 'owa-mail-reading-pane-view';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { SxSLayerView } from 'owa-sxs';
import getExtraCustomDataForDatapoint from 'owa-sxs-store/lib/utils/getExtraCustomDataForDatapoint';
import { ImageGalleryItem, LoadedInfo } from 'owa-sxsdata';
import * as React from 'react';
import { render } from 'react-dom';
import {
    EditMode,
    lazyOnBeforeImageGalleryItemSelected,
    lazyOnChangeByImageGallery,
    lazySxSStoreModule,
    lazyTryCloseSxS,
    SxSCustomization,
    SxSEventNames,
    SxSReadingPaneMode,
    getOrCreateSxSStoreData,
    SXSID_MAIN_WINDOW,
    SXSV2_CONTAINER_ID,
} from 'owa-sxs-store';
import { isDeepLink } from 'owa-url';
import getOpenCloudyDocumentInNewTabButton from './commandBar/getOpenCloudyDocumentInNewTabButton';

const styles = require('./SxSV2.scss');

interface SxSV2Props {
    sxsId?: string; // pass if SxS is supported and can be supported with multiple views
}

export const SxSV2 = observer(function SxSV2(props: SxSV2Props) {
    const rightHandSideHtml = React.useRef<string>(null);
    const rightHandSideContainerDiv = React.useRef<HTMLDivElement>(null);

    const targetWindow = React.useContext(ProjectionContext);
    const isInPopOut: boolean = targetWindow != window;
    const sxsId = props.sxsId;
    const sxsStore = getOrCreateSxSStoreData(sxsId);

    React.useEffect(
        () => {
            const readingPaneState = sxsStore.readingPaneState;
            if (
                readingPaneState &&
                readingPaneState.mode === SxSReadingPaneMode.ConversationReadingPane &&
                getConversationReadingPaneViewState()?.conversationId.Id !==
                    readingPaneState.conversationId.Id
            ) {
                lazyTryCloseSxS.importAndExecute('conversationChanged', sxsId);
            }
        }
        // No dependency array, so it runs every render
    );
    const renderRightHandSideFake = () => {
        /* tslint:disable:react-no-dangerous-html */
        return rightHandSideHtml.current ? (
            <div
                className={styles.rightHandSideContainer}
                dangerouslySetInnerHTML={{ __html: rightHandSideHtml.current }}
            />
        ) : null;
        /* tslint:enable:react-no-dangerous-html */
    };
    const renderRightHandSide = () => {
        const rightHandSide = renderRightHandSideInternal(sxsId);
        if (rightHandSide) {
            return (
                <div
                    className={styles.rightHandSideContainer}
                    ref={r => (rightHandSideContainerDiv.current = r)}>
                    {rightHandSide}
                </div>
            );
        } else {
            return null;
        }
    };
    const onImageGalleryItemSelected = async () => {
        const onChangeByImageGallery = await lazyOnChangeByImageGallery.import();
        onChangeByImageGallery(sxsId);
    };
    const getItemIdForLogging = () => {
        return getOrCreateSxSStoreData(sxsId).editState?.itemId?.Id;
    };
    const onAnimationEnd = () => {
        getOrCreateSxSStoreData(sxsId).eventManager.emit(SxSEventNames.AnimationEndEvent);
    };
    const popOutButtonClickedProjection = async () => {
        const useNewSxSId: boolean = await popOutSxS(sxsId, targetWindow);
        // if we are re-using the old sxsId, we don't close the original SxS as it would close the new one as well
        // otherwise, we can safely close SxS
        if (useNewSxSId) {
            closeSxSInternal('popOutButton');
        }
    };
    const popOutButtonClicked = async () => {
        const startTime = new Date().getTime();
        const sxsStore = getOrCreateSxSStoreData(sxsId);
        const sxsStoreModule = await lazySxSStoreModule.import();
        sxsStoreModule.openSxSInANewWindow(
            sxsStore.editState.itemId,
            sxsStore.extendedViewState.attachmentId
                ? sxsStore.extendedViewState.attachmentId.Id
                : null,
            sxsStore.editState.mode === EditMode.Edit,
            startTime
        );
        sxsStoreModule.tryCloseSxS('popOutButton', sxsId);
    };
    const closeSxSInternal = async (closeReason: string) => {
        const tryCloseSxS = await lazyTryCloseSxS.import();
        tryCloseSxS(closeReason, sxsId);
    };
    const closeSxS = () => {
        closeSxSInternal('closeButton');
    };
    const onError = (errorMessage: string, error?: Error) => {
        const sxsStore = getOrCreateSxSStoreData(sxsId);
        sxsStore.eventManager.emit(SxSEventNames.PreviewErrorEvent, { errorMessage, error });
        sxsStore.eventManager.emit(SxSEventNames.PreviewLoadOrErrorEvent, { errorMessage, error });
    };
    const onLoad = (loadedInfo?: LoadedInfo) => {
        const sxsStore = getOrCreateSxSStoreData(sxsId);
        sxsStore.eventManager.emit(SxSEventNames.PreviewLoadEvent, { loadedInfo });
        sxsStore.eventManager.emit(SxSEventNames.PreviewLoadOrErrorEvent, { loadedInfo });
    };
    const renderSxSCustomPreview = () => {
        const sxsStore = getOrCreateSxSStoreData(sxsId);
        return (
            <SxSCustomView
                onError={onError}
                onLoad={onLoad}
                viewState={sxsStore.extendedViewState}
                customPreviewState={sxsStore.customPreviewState}
            />
        );
    };
    const getCustomPropertiesForSigsInternal = () => {
        return getCustomPropertiesForSigs(sxsId);
    };
    const getExtraCustomDataForDatapointInternal = () => {
        return getExtraCustomDataForDatapoint(sxsId);
    };
    const getOpenCloudyDocumentInNewTabButtonInternal = () => {
        return getOpenCloudyDocumentInNewTabButton(sxsId);
    };

    if (!sxsStore.shouldShow) {
        return null;
    }
    if (sxsStore.shouldUseFakeRightHandSide) {
        if (!rightHandSideHtml.current && rightHandSideContainerDiv.current) {
            rightHandSideHtml.current = rightHandSideContainerDiv.current.innerHTML;
        }
    } else {
        rightHandSideHtml.current = null;
    }
    const useFakeRightHandSide =
        sxsStore.shouldUseFakeRightHandSide && sxsStore.isUsingFakeRightHandSide;
    const hideOwaHeader =
        sxsStore.customization === SxSCustomization.FilesHub ||
        sxsStore.customization === SxSCustomization.DeepLink ||
        sxsStore.customization === SxSCustomization.PopOut;
    const hideCloseButton =
        sxsStore.customization === SxSCustomization.DeepLink ||
        sxsStore.customization === SxSCustomization.PopOut;
    const modal =
        sxsStore.customization !== SxSCustomization.DeepLink &&
        sxsStore.customization !== SxSCustomization.PopOut;
    const isSxSProjectionPopOutEnabled =
        sxsStore.customization != SxSCustomization.FilesHub && isProjectionPopOutEnabled();
    const showPopOutButton =
        (isSxSProjectionPopOutEnabled ||
            (sxsStore.editState.itemId && sxsStore.extendedViewState.attachmentId)) &&
        (sxsStore.customization === SxSCustomization.Mail ||
            sxsStore.customization === SxSCustomization.FilesHub);

    return (
        <SxSLayerView
            commandBarButtons={getCommandBarButtons(sxsId)}
            isInPopOut={isInPopOut}
            getCustomPropertiesForSigs={getCustomPropertiesForSigsInternal}
            getExtraCustomDataForDatapoint={getExtraCustomDataForDatapointInternal}
            getOpenCloudyDocumentInNewTabButton={getOpenCloudyDocumentInNewTabButtonInternal}
            getItemIdForLogging={getItemIdForLogging}
            hideCloseButton={hideCloseButton}
            hideOwaHeader={hideOwaHeader}
            modal={modal}
            newStyle={true}
            onAnimationEnd={onAnimationEnd}
            onBeforeImageGalleryItemSelected={onBeforeImageGalleryItemSelected}
            onClose={closeSxS}
            onError={onError}
            onFocusRightHandPane={lazyResetFocus.importAndExecute}
            onImageGalleryItemSelected={onImageGalleryItemSelected}
            onLoad={onLoad}
            onPopOut={
                showPopOutButton
                    ? isSxSProjectionPopOutEnabled
                        ? popOutButtonClickedProjection
                        : popOutButtonClicked
                    : null
            }
            renderCustomView={renderSxSCustomPreview}
            shouldHide={sxsStore.isInvisible}
            viewState={sxsStore.viewState}
            notUseFocusTrapZone={sxsStore.customization === SxSCustomization.PopOut}>
            {useFakeRightHandSide ? renderRightHandSideFake() : renderRightHandSide()}
        </SxSLayerView>
    );
});

export default function insertSxSV2IntoDomIfNeeded(targetWindow: Window, sxsId: string) {
    if ((targetWindow || window).document.getElementById(SXSV2_CONTAINER_ID)) {
        return;
    }

    const containerDiv = document.createElement('div');
    containerDiv.id = SXSV2_CONTAINER_ID;
    sxsId = sxsId || SXSID_MAIN_WINDOW;
    targetWindow = targetWindow || window;
    targetWindow.document.body.appendChild(containerDiv);
    render(
        <ProjectionContext.Provider value={targetWindow}>
            <SxSV2 sxsId={sxsId} />
        </ProjectionContext.Provider>,
        containerDiv
    );
}

function isProjectionPopOutEnabled() {
    // projection window that hosts a deeplink in a new OWA session
    return !isDeepLink() && isFeatureEnabled('mail-popout-projection');
}

async function onBeforeImageGalleryItemSelected(imageGalleryItem: ImageGalleryItem) {
    const onBeforeImageGalleryItemSelected = await lazyOnBeforeImageGalleryItemSelected.import();
    await onBeforeImageGalleryItemSelected(imageGalleryItem);
}

function renderRightHandSideInternal(sxsId: string) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const composeViewState = sxsStore.composeId && composeStore.viewStates.get(sxsStore.composeId);
    if (composeViewState) {
        return <FullCompose viewState={composeViewState} />;
    }
    const readingPaneState = sxsStore.readingPaneState;
    if (readingPaneState) {
        if (
            readingPaneState.mode === SxSReadingPaneMode.ConversationReadingPane &&
            getConversationReadingPaneViewState()?.conversationId.Id ===
                readingPaneState.conversationId.Id
        ) {
            return (
                <ConversationReadingPane
                    conversationId={readingPaneState.conversationId}
                    conversationSubject={readingPaneState.conversationSubject}
                    conversationCategories={readingPaneState.conversationCategories}
                    shouldNotFocusOnInitialRender={true}
                    instrumentationContext={
                        getConversationReadingPaneViewState(readingPaneState.conversationId.Id)
                            .instrumentationContext
                    }
                    isSxS={true}
                />
            );
        } else if (readingPaneState.mode === SxSReadingPaneMode.ItemReadingPane) {
            return (
                <ItemReadingPane
                    itemId={readingPaneState.itemId}
                    itemReadingPaneViewState={getItemReadingPaneViewState(
                        readingPaneState.itemId.Id
                    )}
                    isSxS={true}
                />
            );
        }
    }
    return null;
}
